import React, {Component} from 'react';
import { injectIntl } from 'react-intl';
import classNames from 'classnames';
import MakeHash from 'make-hash';
import moment from 'moment';
import Endpoints from 'helpers/enums/endpoints';
import GridRow from 'components/grid/grid-row';
import GridCol from 'components/grid/grid-col';
import Hero from 'components/heroes/hero';
import Button from 'components/button/button';
import Session from 'pages/sessions/_session';
import SingleSession from 'pages/sessions/_single-session';
import Timeline from 'components/timeline/timeline';
import Dropdown from 'components/dropdown/dropdown';
import SessionsNormalizer from 'normalizers/sessions-normalizer';
import SessionFilterHelper from 'helpers/utilities/session-filters';
import Page from 'components/page/page';
import LoaderHelper from 'helpers/utilities/loader-helper';
import messages from './messages';

class Sessions extends Component {
  constructor(props) {
    super(props);

    const { intl } = props;

    this.locationFilter = undefined;
    this.typeFilter = undefined;

    /**
     * Flag to track the mounted state of the component so setState is not
     * called on a component that has been destroyed
     *
     * @type {Boolean}
     */
    this._isMounted = false;

    this.state = {
      'noSessions': false,
      'serverError': false,
      'sessionTypeFilterOptions': [],
      'sessionLocationFilterOptions': [],
      'sessions': [],
      'loading': true
    };

    this.page = {
      'heading': intl.formatMessage(messages.heading),
      'intro': intl.formatMessage(messages.intro),
      'empty': intl.formatMessage(messages.empty),
      'error': intl.formatMessage(messages.loading_error)
    };

    this.setUpHandlers();
  }

  /**
   * React Lifecycle Hook
   * Invoked once, immediately after the initial rendering occurs.
   *
   * @method  componentDidMount
   */
  componentDidMount() {
    this._isMounted = true;

    this.getData();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  /**
   * This is an example method that fetches data from an API
   *
   * @method getData
   */
  getData() {
    const startTime = moment();
    this.setState({
      'serverError': false,
    });

    this.props.apiService.get(
      Endpoints.sessions,
      this.handleError.bind(this)
    )
    .then(data => {
      const normalizedData = SessionsNormalizer.normalize(data);

      this.setState({
        'noSessions': normalizedData.length === 0,
        'data': normalizedData.sessionObjects,
        'sessions': normalizedData.sessionObjects,
        'sessionTypeFilterOptions': SessionFilterHelper.buildTypeFilters(normalizedData, this.filterSessions.bind(this)),
        'sessionLocationFilterOptions': SessionFilterHelper.buildLocationFilters(normalizedData, this.filterSessions.bind(this)),
      });
    })
    .then(() => {
      LoaderHelper(startTime, () => {
        if (this._isMounted) {
          this.setState({
            'loading': false
          });
        }
      });
    });
  }

   handleError(error) {
     this.setState({
       'serverError': true,
       'loading': false
     });
   }

  /**
   * Binds any handlers to the correct context
   *
   * @method  setUpHandlers
   */
  setUpHandlers() {
    this.findSessions = this.findSessions.bind(this);
  }

  findSessions() {
    this.getData();
  }

  filterSessions(data) {
    let filtered = this.state.data;
    const filterBy = data.filterBy;

    if (filterBy === 'shop_uuid') {
      this.locationFilter = data.filter === 'all' ? undefined : data.filter;
    }
    else {
      this.typeFilter = data.filter === 'all' ? undefined : data.filter;
    }

    if (this.locationFilter) {
      filtered = filtered.filter(session => {
        return session.shop_uuid === this.locationFilter;
      });
    }

    if (this.typeFilter) {
      filtered = filtered.filter(session => {
        return session.session_type === this.typeFilter;
      });
    }

    this.setState({ 'sessions': filtered });
  }

  _renderSessions() {
    const manySessions = () => {
      return this.state.sessions.map((session, i) => {
        return (
          <Session
            key={MakeHash(`session-${session.uuid}`)}
            index={i}
            data={session}
          />
        );
      });
    };

    return this.state.sessions.length > 1 ? manySessions() : (
      <SingleSession
        data={this.state.sessions[0]}
      />
    )
  }

  _renderBody() {
    if (this.state.loading || this.state.noSessions) return;

    const { intl } = this.props;
    const sessions = this._renderSessions();

    return (
      <div className="container">
        <section className="tier">
          <div className="block">
            <div className="block-item">
              <Dropdown
                animate="bottomRight"
                label={intl.formatMessage(messages.session_type_filter_label)}
                buttonOptions={this.state.sessionTypeFilterOptions}
                theme="body"
                triggerTxt="all"
                dynamicTrigger
              />
            </div>
          </div>
          <div className="set mix-set_alignCenter">
            <Timeline vertOffset="7" leftOffset="35" adjustForMobile="true" key={this.state.sessions.length}/>
            {sessions}
          </div>
        </section>
      </div>
    );
  }

  _renderSupportingText() {
    let text = this.page.intro;

    if (this.state.serverError) {
      text = this.page.error;
    } else if (this.state.noSessions && !this.state.serverError) {
      text = this.page.empty;
    }

    return text;
  }

  _renderHero() {
    const { intl } = this.props;
    const findSessionsMessage = this.state.serverError ? messages.loading_error_cta_label : messages.empty_cta_label
    const supportingTxt = this._renderSupportingText();
    const heroActions = !this.state.noSessions ? '' : (
      <Button
        animateOnClick
        className="mix-btn_mobile"
        theme="dark"
        onClick={this.findSessions}
      >
        {intl.formatMessage(findSessionsMessage)}
      </Button>
    );

    return (
      <Hero
        title={this.page.heading}
        supportingTxt={supportingTxt}
        serverError={this.state.serverError}
      >
        {heroActions}
      </Hero>
    )
  }

  render() {
    const pageHero = this._renderHero();

    return (
      <Page
        isLoading={this.state.loading}
        fullTexture={this.state.loading || this.state.noSessions}
        title={this.page.heading}
        pageHero={pageHero}
        mainClasses="page-main_megaTop"
      >
        {this._renderBody()}
      </Page>
    );
  }
}

export default injectIntl(Sessions);



// WEBPACK FOOTER //
// ./src/client/pages/sessions/sessions.jsx